// src/components/managed-sources/LaunchProgress.tsx

import {
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Timeline,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCheck,
  IconDatabase,
  IconLoader,
  IconPlayerStop,
  IconRocket,
  IconX,
} from "@tabler/icons-react";
import { Link } from "@/routing/accountRouting";

import {
  isLaunchTerminal,
  launchPhase,
  type LaunchState,
} from "@/components/managed-sources/launchPhase";
import { useManagedSourcePoll } from "@/components/managed-sources/useManagedSourcePoll";
import type { ManagedSource } from "@/types/managedSources";

type Props = {
  /** The source that was just created — the poll's starting point. */
  createdSource: ManagedSource;
  connectorLabel: string;
  isTransformOnly: boolean;
  /** e.g. "~13 months" — already formatted by the caller. */
  backfillLabel: string;
  /** e.g. "daily" — lower-cased schedule name. */
  scheduleLabel: string;
  onClose: () => void;
};

const SOURCES_ROUTE = "/data-management/sources";

type Step = { title: string; description: string; icon: React.ReactNode };

function buildSteps(
  isTransformOnly: boolean,
  backfillLabel: string,
  scheduleLabel: string,
): Step[] {
  const provisioning: Step = {
    title: "Provisioning",
    description: isTransformOnly
      ? "Configuring the transform pipeline"
      : "Setting up BigQuery storage and configuring the pipeline",
    icon: <IconRocket size={14} />,
  };
  const active: Step = {
    title: "Active",
    description: `Automatic ${isTransformOnly ? "transforms" : "syncs"} on your ${scheduleLabel} schedule`,
    icon: <IconDatabase size={14} />,
  };
  if (isTransformOnly) return [provisioning, active];
  return [
    provisioning,
    {
      title: "Backfill",
      description: `Loading ${backfillLabel} of historical data`,
      icon: <IconLoader size={14} />,
    },
    active,
  ];
}

/**
 * Colour of the step in progress. Mantine fills the active bullet with this
 * and puts white on top (Timeline.css — `[data-active][data-with-child]`), so
 * the bullet's contents must be white-on-solid, never a tinted ThemeIcon: a
 * `variant="light"` icon is 10% opaque and renders blue-on-blue, which is how
 * this screen ended up showing a featureless dot.
 */
function activeColor(state: LaunchState): string {
  switch (state) {
    case "failed":
      return "red";
    case "retrying":
      // Not yellow: the bullet is filled and its glyph is white, and white on
      // Mantine yellow is unreadable. Orange still reads as "attention, not
      // failure".
      return "orange";
    case "stopped":
      return "gray";
    default:
      return "blue";
  }
}

/** What sits in the bullet of the step currently in progress. */
function activeBullet(state: LaunchState): React.ReactNode {
  switch (state) {
    case "failed":
      return <IconX size={14} />;
    case "stopped":
      return <IconPlayerStop size={14} />;
    default:
      // Running and retrying are the states where something is actually
      // happening — the spinner is the only thing on this screen that says so.
      return <Loader size={14} color="white" />;
  }
}

const HEADER: Record<
  LaunchState,
  { color: string; icon: React.ReactNode; title: string }
> = {
  running: { color: "green", icon: <IconCheck size={32} />, title: "Pipeline launched" },
  retrying: {
    color: "orange",
    icon: <IconAlertTriangle size={32} />,
    title: "Retrying",
  },
  done: { color: "green", icon: <IconCheck size={32} />, title: "Pipeline is live" },
  failed: { color: "red", icon: <IconX size={32} />, title: "Pipeline needs attention" },
  stopped: {
    color: "gray",
    icon: <IconPlayerStop size={32} />,
    title: "Setup stopped",
  },
};

/**
 * The wizard's closing screen. Renders the real state of the pipeline that was
 * just created, polled until it settles.
 *
 * Its own component rather than a branch inside ReviewStep so the poll starts
 * when this screen appears and stops when it unmounts — a hook in ReviewStep
 * would run through the whole wizard, including the steps before submit.
 */
export function LaunchProgress({
  createdSource,
  connectorLabel,
  isTransformOnly,
  backfillLabel,
  scheduleLabel,
  onClose,
}: Props) {
  const { source, gone, cappedOut } = useManagedSourcePoll(createdSource.id, {
    enabled: true,
    isTerminal: isLaunchTerminal,
  });

  // Fall back to the source we were handed until the first poll lands.
  const live = source ?? createdSource;
  const phase = launchPhase(live);
  const steps = buildSteps(isTransformOnly, backfillLabel, scheduleLabel);
  const header = HEADER[phase.state];

  // Only the ingest path has a customer-safe message to show; the transform
  // path deliberately carries none (see launchPhase).
  const detail =
    phase.state === "failed"
      ? phase.errorMessage ??
        `Your ${connectorLabel} pipeline could not finish setting up. Open the source to see what went wrong.`
      : phase.state === "stopped"
        ? "The first run was cancelled. You can start it again from the source."
        : phase.state === "done"
          ? `Your ${connectorLabel} data is ready.`
          : phase.state === "retrying"
            ? "The first run didn't succeed. We're trying again automatically."
            : `Your ${connectorLabel} ${isTransformOnly ? "transform" : "data"} pipeline is starting up. You can close this window — it keeps running.`;

  return (
    <Stack gap="lg" align="center" py="xl">
      <ThemeIcon size={64} radius="xl" color={header.color} variant="light">
        {header.icon}
      </ThemeIcon>

      <Stack gap={4} align="center">
        <Text fw={700} size="xl" ta="center">
          {header.title}
        </Text>
        <Text size="sm" c="dimmed" ta="center" maw={420}>
          {detail}
        </Text>
      </Stack>

      <Paper withBorder p="lg" radius="md" w="100%" maw={480}>
        {/* Mantine fills the bullet of every item up to `active` with that
            item's colour and puts white on top, so the colour goes on the
            Item and the bullet holds only a white glyph. Nesting a tinted
            ThemeIcon here is what produced blue-on-blue dots and green ticks
            floating on blue discs. */}
        <Timeline active={phase.activeIndex} bulletSize={28} lineWidth={2}>
          {steps.map((step, index) => {
            const done = index < phase.activeIndex;
            const current = index === phase.activeIndex;
            return (
              <Timeline.Item
                key={step.title}
                color={done ? "green" : current ? activeColor(phase.state) : undefined}
                lineVariant={index === steps.length - 1 ? "dashed" : "solid"}
                bullet={
                  done ? (
                    <IconCheck size={15} stroke={3} />
                  ) : current ? (
                    activeBullet(phase.state)
                  ) : (
                    step.icon
                  )
                }
                title={
                  <Text
                    fw={done || current ? 600 : 500}
                    size="sm"
                    c={done || current ? undefined : "dimmed"}
                  >
                    {step.title}
                  </Text>
                }
              >
                <Text size="xs" c="dimmed">
                  {step.description}
                </Text>
              </Timeline.Item>
            );
          })}
        </Timeline>

        {/* Progress can legitimately stop being observable: a source whose
            categories exclude the watermark-writing asset never reports its
            backfill as complete. Say so instead of spinning forever. */}
        {(cappedOut || gone) &&
          (phase.state === "running" || phase.state === "retrying") && (
          <Text size="xs" c="dimmed" mt="md" ta="center">
            Setup is taking longer than usual. It continues in the background —
            check the source for its current status.
          </Text>
        )}
      </Paper>

      <Group gap="sm" w="100%" maw={480} grow>
        <Button component={Link} to={SOURCES_ROUTE} onClick={onClose} variant="light">
          View source
        </Button>
        <Button onClick={onClose} variant={phase.state === "failed" ? "light" : "subtle"}>
          Close
        </Button>
      </Group>
    </Stack>
  );
}
